// src/features/upload/components/UploadQueueStatusBanner.tsx

import { Alert, Button, Group, Progress, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconUpload } from '@tabler/icons-react';
import { UploadUtils } from '../UploadUtils';
import type { UploadItem } from '@/lib/types/Upload';

interface UploadQueueStatusBannerProps {
  items: UploadItem[];
  /** Show a Sign out button in the all-received state (upload-only users). */
  showSignOut: boolean;
  onSignOut: () => void;
}

/**
 * Aggregate queue status for the full-page upload experience. The key message
 * is the all-received state: uploaders must know that once files reach the
 * server they can leave — processing needs no open tab.
 */
export function UploadQueueStatusBanner({ items, showSignOut, onSignOut }: UploadQueueStatusBannerProps) {
  const phase = UploadUtils.getQueuePhase(items);
  if (phase === 'empty') return null;

  const counts = UploadUtils.getQueueCounts(items);

  if (phase === 'sending') {
    const sendingItems = items.filter(
      (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading'
    );
    const totalBytes = sendingItems.reduce((sum, i) => sum + i.bytesTotal, 0);
    const uploadedBytes = sendingItems.reduce((sum, i) => sum + i.bytesUploaded, 0);
    const progress = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

    return (
      <Alert
        icon={<IconUpload size={18} />}
        color="blue"
        variant="light"
        title={`Sending ${counts.inFlight} file${counts.inFlight !== 1 ? 's' : ''}…`}
      >
        <Stack gap="xs">
          <Text size="sm">Keep this tab open until sending finishes.</Text>
          <Progress value={progress} size="sm" color="blue" />
        </Stack>
      </Alert>
    );
  }

  if (phase === 'all-received') {
    const parts: string[] = [];
    if (counts.processing > 0) parts.push(`${counts.processing} processing`);
    if (counts.ready > 0) parts.push(`${counts.ready} ready`);

    return (
      <Alert icon={<IconCircleCheck size={18} />} color="green" variant="light" title="All files received">
        <Stack gap="xs">
          <Text size="sm">
            Processing continues automatically on our servers — you can safely sign out or close this tab.
          </Text>
          <Group gap="md">
            {parts.length > 0 && (
              <Text size="sm" c="dimmed">
                {parts.join(' · ')}
              </Text>
            )}
            {counts.failed > 0 && (
              <Text size="sm" c="red">
                {counts.failed} file{counts.failed !== 1 ? 's' : ''} failed — retry below.
              </Text>
            )}
          </Group>
          {showSignOut && (
            <Group>
              <Button size="xs" variant="default" onClick={onSignOut}>
                Sign out
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>
    );
  }

  // 'attention' — only failures (and cancels) remain
  return (
    <Alert
      icon={<IconAlertTriangle size={18} />}
      color="orange"
      variant="light"
      title={`${counts.failed} upload${counts.failed !== 1 ? 's' : ''} failed`}
    >
      <Text size="sm">Use retry on a file below to try again.</Text>
    </Alert>
  );
}
